/** Résolution des bases API Chat (suite root / api_suite / api_chat). */

const DEFAULT_SUITE_HUB_ORIGIN = "https://auth.app.aureliasuite.fr";

function readEnv(key: string): string | undefined {
  const raw = import.meta.env[key];
  if (typeof raw !== "string") return undefined;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed.replace(/\/+$/u, "") : undefined;
}

/** PWA sur vhost dédié (chat.app) — Alias /api_suite + /api_chat same-origin. */
export function isChatStandalone(): boolean {
  if (import.meta.env.VITE_CHAT_STANDALONE === "true") return true;
  // Repli runtime si .env.production absent au build (fichier souvent gitignoré)
  if (typeof window !== "undefined") {
    const host = window.location.hostname.toLowerCase();
    if (host === "chat.app.aureliasuite.fr" || host.startsWith("chat.app.")) {
      return true;
    }
  }
  return false;
}

export function getSuiteHubOrigin(): string {
  return readEnv("VITE_SUITE_HUB_ORIGIN") ?? DEFAULT_SUITE_HUB_ORIGIN;
}

function resolveSuiteRootFromPathname(): string | null {
  if (typeof window === "undefined") return null;
  if (isChatStandalone()) return null;
  const p = window.location.pathname;
  try {
    if (p.includes("/chat/dist")) {
      return new URL("../../", window.location.href).href.replace(/\/$/, "");
    }
    if (/\/chat\/(index\.html)?$/i.test(p) || p.endsWith("/chat/")) {
      return new URL("../", window.location.href).href.replace(/\/$/, "");
    }
    const suiteIdx = p.indexOf("/suite/");
    if (suiteIdx >= 0) {
      const origin = window.location.origin;
      return `${origin}${p.slice(0, suiteIdx + "/suite".length)}`;
    }
    if (p === "/suite" || p.endsWith("/suite")) {
      return `${window.location.origin}${p.replace(/\/$/, "")}`;
    }
  } catch {
    return null;
  }
  return null;
}

function absoluteAppPath(path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  if (typeof window === "undefined") return normalized;
  return `${window.location.origin}${normalized}`;
}

/**
 * Base api_suite.
 * Standalone : same-origin absolu (new URL exige une base http(s)).
 */
export function getResolvedApiSuiteBase(): string | null {
  const fromEnv = import.meta.env.VITE_API_SUITE_BASE;
  if (fromEnv && String(fromEnv).trim() !== "") {
    return String(fromEnv).replace(/\/$/, "");
  }
  if (isChatStandalone()) {
    return absoluteAppPath("/api_suite");
  }
  const suiteRoot = resolveSuiteRootFromPathname();
  if (!suiteRoot) return null;
  return `${suiteRoot}/api_suite`;
}

/**
 * SSO verify (cookie-less, CORS *).
 * PWA standalone → auth.app direct (connexion sans dépendre de l’alias vhost).
 */
export function getSsoVerifyBase(): string | null {
  if (isChatStandalone()) {
    return `${getSuiteHubOrigin()}/api_suite`;
  }
  return getResolvedApiSuiteBase();
}

export function getSuiteHubWhoamiUrl(): string | null {
  // PWA standalone : pas de poll whoami (comme Mail — évite boucle session lost).
  if (isChatStandalone()) return null;
  const base = getResolvedApiSuiteBase();
  return base ? `${base}/session/whoami.php` : null;
}

export function getChatPingUrl(): string | null {
  const base = getResolvedApiSuiteBase();
  return base ? `${base}/chat-ping.php` : null;
}

/** Base `api_chat` (conversations, messages, poll…). */
export function getChatApiBase(): string | null {
  if (isChatStandalone()) {
    return absoluteAppPath("/api_chat");
  }
  const suiteApi = getResolvedApiSuiteBase();
  if (!suiteApi) {
    const suiteRoot = resolveSuiteRootFromPathname();
    return suiteRoot ? `${suiteRoot}/api_chat` : null;
  }
  const trimmed = suiteApi.replace(/\/$/, "");
  const chatBase = trimmed.replace(/\/api_suite$/i, "/api_chat");
  if (chatBase === trimmed) {
    try {
      return new URL("../api_chat", `${trimmed}/`).href.replace(/\/$/, "");
    } catch {
      return null;
    }
  }
  return chatBase;
}

export function chatAuthHeaders(suiteUserId: string | null): Record<string, string> {
  const headers: Record<string, string> = {};
  if (suiteUserId && /^\d+$/.test(suiteUserId)) {
    headers["X-User-Id"] = suiteUserId;
  }
  return headers;
}

export async function fetchChatPing(suiteUserId: string | null): Promise<{
  success: boolean;
  userId?: number;
  message?: string;
  error?: string;
}> {
  const url = getChatPingUrl();
  if (!url) {
    return {
      success: false,
      error: "no_api_url",
      message: isChatStandalone()
        ? "API suite introuvable — vérifier Alias /api_suite sur le vhost chat.app."
        : "Définir VITE_API_SUITE_BASE dans .env.development pour tester l’API en local.",
    };
  }
  const headers = chatAuthHeaders(suiteUserId);
  try {
    const ctrl = new AbortController();
    const t = window.setTimeout(() => ctrl.abort(), 15000);
    const res = await fetch(url, {
      method: "GET",
      headers,
      credentials: "include",
      signal: ctrl.signal,
    });
    window.clearTimeout(t);
    let data: Record<string, unknown>;
    try {
      data = (await res.json()) as Record<string, unknown>;
    } catch {
      return {
        success: false,
        error: "invalid_json",
        message: isChatStandalone()
          ? `Réponse non JSON (${res.status}) — Alias /api_suite manquant ou PHP en erreur (${url}).`
          : `Réponse non JSON (${res.status}). Vérifier l’URL : ${url}`,
      };
    }
    if (!res.ok) {
      return {
        success: false,
        error: typeof data.error === "string" ? data.error : `http_${res.status}`,
        message: typeof data.message === "string" ? data.message : res.statusText,
      };
    }
    return data as { success: boolean; userId?: number; message?: string; error?: string };
  } catch (e) {
    const aborted =
      (e instanceof Error && e.name === "AbortError") ||
      (typeof DOMException !== "undefined" && e instanceof DOMException && e.name === "AbortError");
    return {
      success: false,
      error: "network",
      message: aborted
        ? "Délai dépassé (15 s) — vérifier Alias /api_suite sur le vhost."
        : e instanceof Error
          ? e.message
          : "Erreur réseau",
    };
  }
}
